// src/components/SimpleComponents/SimpleAvatar.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useId, useState, useEffect } from 'react';
import { Avatar, theme } from 'antd';
import {
  DEFAULT_BACK_COLOR_ACCENT,
  DEFAULT_FORE_COLOR,
  DEFAULT_BACK_COLOR_ACCENT_DARK,
  DEFAULT_FORE_COLOR_DARK,
} from 'constants/Constants';
import utils from 'utils/utils';
import app from 'configs/ConfigIndex';
import useZustand from 'hooks/useZustand';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const SHAPE_CONFIG = {
  square: {
    shape: 'square',
    borderRadius: 8,
  },
  circle: {
    shape: 'circle',
    borderRadius: '50%',
  },
};

const loadingAreaStyle = { top: -4, left: -4, background: 'transparent' };

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
/**
 * SimpleAvatar - Avatar component with transparent PNG support
 * Encapsulates the working pattern from CardHeaderAvatar.jsx
 */
const SimpleAvatar = ({
  src,
  name,
  size = 40,
  shape = 'circle',
  backColor: backColorProp,
  foreColor: foreColorProp,
  borderColor: borderColorProp,
  style = {},
  className = '',
  imgStyle,
  showBorder = true,
  showShadow = true,
  loading = false,
  loadingName,
  hideLoading = false,
  shapeStyle = {},
}) => {
  const { token } = theme.useToken();

  // Track if image failed to load (to show initials as fallback)
  const [imageFailed, setImageFailed] = useState(false);

  // Reset imageFailed when src changes (new image to try)
  useEffect(() => {
    if (src) {
      setImageFailed(false);
    }
  }, [src]);

  // Generate unique ID for dynamic img styles
  const uniqueId = useId();
  const hasCustomImgStyles = imgStyle || shapeStyle?.padding;
  const imgStyleClass = hasCustomImgStyles ? `simple-avatar-${uniqueId.replace(/:/g, '')}` : '';

  // Detect theme for default colors
  const currentTheme = useZustand('theme', 'currentTheme');
  const isDarkMode = currentTheme === 'dark';

  // Use theme-aware defaults if no color is provided
  const defaultBackColor = isDarkMode ? DEFAULT_BACK_COLOR_ACCENT_DARK : DEFAULT_BACK_COLOR_ACCENT;
  const defaultForeColor = isDarkMode ? DEFAULT_FORE_COLOR_DARK : DEFAULT_FORE_COLOR;

  const backColor = backColorProp ?? defaultBackColor;
  const foreColor = foreColorProp ?? defaultForeColor;

  // Get shape config
  const shapeConfig = SHAPE_CONFIG[shape] || SHAPE_CONFIG.circle;

  // Calculate initials
  const initials = name ? utils.getNameInitial(name) : '';
  const hasInitials = initials.trim().length > 0;

  // Calculate font size based on avatar size (guard string sizes: 'small'=24, 'default'=32, 'large'=40)
  const _sz = typeof size === 'string' ? ({ small: 24, default: 32, large: 40 }[size] ?? 32) : size;
  const fontSize = _sz <= 24 ? 10 : _sz <= 32 ? 12 : _sz <= 48 ? 16 : _sz * 0.35;

  // Border logic:
  // 1. If borderColorProp provided → use it (e.g., for overlapping groups needing white separator)
  // 2. If using default backColor → neutral grey (AntD token)
  // 3. Otherwise → use backColor (blends with background)
  const isUsingDefaultColor = backColor === defaultBackColor;
  const borderColor = borderColorProp ?? (isUsingDefaultColor ? token.colorBorder : backColor);
  const border = showBorder ? `1px solid ${borderColor}` : 'none';

  // Shadow
  const boxShadow = showShadow ? '0 2px 6px rgba(0, 0, 0, 0.4)' : 'none';

  // Convert imgStyle object to CSS string
  const imgStyleCss = imgStyle
    ? Object.entries(imgStyle)
        .map(([key, value]) => {
          // Convert camelCase to kebab-case
          const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
          // Add px for numeric values (except unitless properties)
          const cssValue = typeof value === 'number' ? `${value}px` : value;
          return `${cssKey}: ${cssValue} !important`;
        })
        .join('; ')
    : '';

  // Build CSS for padding on img (applied via shapeStyle.padding)
  const paddingCss = shapeStyle?.padding
    ? `padding: ${typeof shapeStyle.padding === 'number' ? `${shapeStyle.padding}px` : shapeStyle.padding} !important; box-sizing: border-box !important`
    : '';

  // Remove padding from shapeStyle (applied via CSS injection instead)
  const { padding: _padding, ...avatarShapeStyle } = shapeStyle || {};

  const wrapperStyle = { position: 'relative', display: 'inline-block', ...style };
  const avatarStyle = { borderRadius: shapeConfig.borderRadius, background: backColor, border, boxShadow, ...avatarShapeStyle };
  const initialsStyle = {
    position: 'absolute', top: '50%', left: '50%',
    transform: 'translate(-50%, -50%)', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    width: '100%', height: '100%', color: foreColor,
    fontSize, fontWeight: 600, pointerEvents: 'none', marginTop: 1,
  };

  return (
    <div
      className={`${className} ${imgStyleClass}`.trim()}
      style={wrapperStyle}
    >
      {/* Inject dynamic styles for img element */}
      {hasCustomImgStyles && (
        <style>{`.${imgStyleClass} .ant-avatar img { ${[imgStyleCss, paddingCss].filter(Boolean).join('; ')} }`}</style>
      )}
      <Avatar
        shape={shapeConfig.shape}
        size={size}
        src={src || undefined}
        onError={() => {
          setImageFailed(true); // Track failure to show initials as fallback
          return true; // Return true to show fallback
        }}
        onLoad={() => {
          setImageFailed(false); // Clear any previous failure
        }}
        style={avatarStyle}
      />

      {/* Initials when no image OR image failed to load */}
      {(!src || imageFailed) && hasInitials && (
        <div style={initialsStyle}>
          {initials}
        </div>
      )}

      {/* Loading area - shows when:
          1. loading prop is true (e.g., uploading image)
          2. loadingName store is active (e.g., refresh data)
          Pass visible={true} when loading, otherwise undefined to let store decide */}
      <SimpleLoadingArea
        name={loadingName}
        visible={!hideLoading && (loading || undefined)}
        offsetTop={0}
        showProgress={true}
        title={false}
        zIndex={10}
        borderRadius={shapeConfig.borderRadius}
        style={loadingAreaStyle}
      />
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimpleAvatar;
